import { useState } from "react";
import { toast } from "sonner";
import { Video } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Panel, toIsoFromLocal } from "@/components/admin/Panel";
import { api, fmtDate, formatErr } from "@/lib/api";

const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const toLocalInput = (iso) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export const InterviewPanel = ({ app, onSaved }) => {
  const tz = app.contact.timezone;
  const [when, setWhen] = useState(app.interview ? toLocalInput(app.interview.scheduled_at) : "");
  const [zoom, setZoom] = useState(app.interview?.zoom_link || "");
  const [busy, setBusy] = useState(false);
  const locked = ["Cash Payment Received", "Active Member", "Declined", "Suspended"].includes(app.status);

  const save = async () => {
    if (!when || !zoom) return toast.error("Pick a time and paste the Zoom link.");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/applications/${app.id}/interview`, { scheduled_at: toIsoFromLocal(when), zoom_link: zoom });
      onSaved(data);
      toast.success("Interview scheduled - email sent to applicant (mocked)");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Zoom interview" testId="panel-interview">
      <p className="text-sm font-bold mb-2">Applicant's preferred times</p>
      <div className="space-y-2 mb-5">
        {app.interview_slots.map((s, i) => (
          <button key={i} type="button" onClick={() => setWhen(toLocalInput(s.utc))} className="w-full text-left rounded-xl border border-border p-3 text-sm hover:border-primary transition-colors" data-testid={`interview-slot-${i + 1}`}>
            <span className="font-bold">{fmtDate(s.utc, tz)}</span> <span className="text-muted-foreground">({tz})</span>
            <span className="block text-xs text-muted-foreground">Your time: {fmtDate(s.utc)} ({LOCAL_TZ})</span>
          </button>
        ))}
      </div>
      {app.interview && (
        <div className="mb-5 rounded-xl bg-purple-500/10 border border-purple-500/30 p-3 text-sm" data-testid="interview-scheduled-info">
          <p className="font-bold flex items-center gap-2"><Video className="h-4 w-4 text-purple-500" />Scheduled {fmtDate(app.interview.scheduled_at, tz)} ({tz})</p>
          <a href={app.interview.zoom_link} target="_blank" rel="noreferrer" className="text-xs text-primary break-all">{app.interview.zoom_link}</a>
        </div>
      )}
      {locked ? <p className="text-sm text-muted-foreground" data-testid="interview-locked-note">Interview scheduling is closed for {app.status} applicants.</p> : (
      <div className="space-y-3">
        <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="h-11 rounded-xl" data-testid="interview-datetime-input" />
        <p className="text-xs text-muted-foreground">Entered in your time zone ({LOCAL_TZ}). The email converts it to the applicant's time zone.</p>
        <Input placeholder="https://zoom.us/j/..." value={zoom} onChange={(e) => setZoom(e.target.value)} className="h-11 rounded-xl" data-testid="interview-zoom-input" />
        <button onClick={save} disabled={busy} className="w-full h-11 rounded-full bg-purple-600 text-white font-bold hover:bg-purple-700 transition-colors disabled:opacity-60" data-testid="interview-schedule-button">
          {app.interview ? "Reschedule & email applicant" : "Schedule & email applicant"}
        </button>
      </div>
      )}
    </Panel>
  );
};
